/**
 * Conversations list page — diagnose conversation state and members.
 * Filter by type, frozen, terminated; search by conversation ID.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AdminConversation,
  type ConversationType,
  fetchConversations,
} from "../../api/admin-conversations";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 20;
const TYPE_OPTIONS = ["all", "h2h", "h2a", "group"] as const;

export function ConversationsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [type, setType] = useState<string>("all");
  const [frozen, setFrozen] = useState<string>("all");
  const [terminated, setTerminated] = useState<string>("all");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchConversations({
        q: query || undefined,
        type: type === "all" ? undefined : (type as ConversationType),
        frozen: frozen === "all" ? undefined : frozen === "yes",
        terminated: terminated === "all" ? undefined : terminated === "yes",
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [query, type, frozen, terminated, offset]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const resetOffset = useCallback((): void => setOffset(0), []);

  return (
    <div>
      <PageHeader
        title={t("conversations.title")}
        description={t("conversations.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <ConvFilters
        query={query}
        type={type}
        frozen={frozen}
        terminated={terminated}
        onQueryChange={setQuery}
        onTypeChange={(v) => {
          setType(v);
          resetOffset();
        }}
        onFrozenChange={(v) => {
          setFrozen(v);
          resetOffset();
        }}
        onTerminatedChange={(v) => {
          setTerminated(v);
          resetOffset();
        }}
        onSearch={resetOffset}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : (
        <>
          <ConvTable
            items={items}
            onRowClick={(id) => navigate(`/content/conversations/${id}`)}
          />
          <ConvPagination offset={offset} total={total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}

// ─── Filters ───

type FilterProps = {
  query: string;
  type: string;
  frozen: string;
  terminated: string;
  onQueryChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onFrozenChange: (v: string) => void;
  onTerminatedChange: (v: string) => void;
  onSearch: () => void;
};

function ConvFilters(p: FilterProps): React.JSX.Element {
  return (
    <div style={s.filterBar}>
      <input
        type="text"
        placeholder={t("conversations.search_placeholder")}
        value={p.query}
        onChange={(e) => p.onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") p.onSearch();
        }}
        style={s.searchInput}
      />
      <select
        value={p.type}
        onChange={(e) => p.onTypeChange(e.target.value)}
        style={s.select}
        aria-label={t("conversations.col_type")}
      >
        {TYPE_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("conversations.all_types") : v.toUpperCase()}
          </option>
        ))}
      </select>
      <select
        value={p.frozen}
        onChange={(e) => p.onFrozenChange(e.target.value)}
        style={s.select}
        aria-label={t("conversations.col_frozen")}
      >
        <option value="all">{t("conversations.all_frozen")}</option>
        <option value="yes">{t("conversations.frozen_yes")}</option>
        <option value="no">{t("conversations.frozen_no")}</option>
      </select>
      <select
        value={p.terminated}
        onChange={(e) => p.onTerminatedChange(e.target.value)}
        style={s.select}
        aria-label={t("conversations.col_terminated")}
      >
        <option value="all">{t("conversations.all_terminated")}</option>
        <option value="yes">{t("conversations.terminated_yes")}</option>
        <option value="no">{t("conversations.terminated_no")}</option>
      </select>
      <button type="button" onClick={p.onSearch} style={s.searchBtn}>
        {t("common.search")}
      </button>
    </div>
  );
}

// ─── Table ───

function ConvTable(props: {
  items: AdminConversation[];
  onRowClick: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("conversations.no_conversations")}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("conversations.col_id")}</th>
            <th style={s.th}>{t("conversations.col_type")}</th>
            <th style={s.th}>{t("conversations.col_members")}</th>
            <th style={s.th}>{t("conversations.col_last_activity")}</th>
            <th style={s.th}>{t("conversations.col_frozen")}</th>
            <th style={s.th}>{t("conversations.col_terminated")}</th>
            <th style={s.th}>{t("conversations.col_created")}</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((c) => (
            <ConvRow key={c.id} conv={c} onClick={props.onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConvRow(props: {
  conv: AdminConversation;
  onClick: (id: string) => void;
}): React.JSX.Element {
  const { conv: c } = props;
  return (
    <tr
      style={s.clickRow}
      onClick={() => props.onClick(c.id)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") props.onClick(c.id);
      }}
    >
      <td style={s.tdMono}>{c.id.slice(0, 8)}</td>
      <td style={s.td}>{c.type.toUpperCase()}</td>
      <td style={s.td}>{c.member_count}</td>
      <td style={s.td}>
        {c.last_message_at ? formatDate(c.last_message_at) : "-"}
      </td>
      <td style={s.td}>
        <BoolBadge value={c.frozen} />
      </td>
      <td style={s.td}>
        <BoolBadge value={c.terminated} />
      </td>
      <td style={s.td}>{formatDate(c.created_at)}</td>
    </tr>
  );
}

function BoolBadge(props: { value: boolean }): React.JSX.Element {
  const bg = props.value ? "#fef2f2" : "#f0fdf4";
  const fg = props.value ? "#991b1b" : "#166534";
  const label = props.value ? t("common.yes") : t("common.no");
  return <span style={{ ...s.badge, background: bg, color: fg }}>{label}</span>;
}

// ─── Pagination ───

function ConvPagination(props: {
  offset: number;
  total: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const page = Math.floor(props.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("conversations.page_info", {
          total: props.total,
          page,
          totalPages,
        })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.offset === 0}
          onClick={() => props.onChange(Math.max(0, props.offset - PAGE_SIZE))}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={props.offset + PAGE_SIZE >= props.total}
          onClick={() => props.onChange(props.offset + PAGE_SIZE)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
